<template>
  <div class="booking-item">
    <v-form ref="form" lazy-validation>
      <v-item-group style="display: flex;" v-if="!derivedReadonly">
        <v-btn
          v-if="qtyMax > 0"
          class="rounded-0"
          elevation="0"
          style="flex: auto;"
          size="large"
          color="primary"
          :disabled="qty === qtyMax || derivedReadonly || qtyMax < 0"
          @click="addAll"
        >Maximale Menge
        </v-btn>
        <v-btn
          v-if="deletable"
          class="rounded-0"
          elevation="0"
          size="large"
          color="error"
          :style="{ flex: qtyMax > 0 ? '' : 'auto' }"
          :disabled="derivedReadonly"
          @click="deletePosition">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
        <v-spacer v-if="!deletable && qtyMax <= 0"/>
        <v-btn
          class="rounded-0"
          elevation="0"
          selected-class="primary--text"
          color="grey-lighten-3"
          size="large"
          border
          @click="resetPosition"
          :disabled="derivedReadonly"
        >
          <v-icon>mdi-restart</v-icon>
        </v-btn>
      </v-item-group>
      <v-card flat>
        <v-card-text>
          <div v-if="hasSubbookings" class="qty-header d-flex justify-space-between">
            <label>Akt. Buchung</label>
            <label>Gesamt</label>
          </div>
          <qty-control
            v-model="qty"
            :max="qtyMax"
            :qtyTotal="pickedCount"
            :unit="position?.Einheit || ''"
            :ean="position?.Eancode"
            title="Menge"
            label="Menge"
            :readonly="derivedReadonly"
            :no-zero="!picklistPicker.picklist?.isInventory"
            :allow-overshoot="picklistPicker.picklist?.allowOvershoot"
          />
          <qty-control
            v-if="qtyGebinde !== null"
            v-model="qtyGebinde"
            :spareQtys="spareQtysGebinde"
            :max="qtyGebindeMax ?? undefined"
            :qtyTotal="pickedGebindeCount ?? undefined"
            :unit="position?.Gebindeinh ?? ''"
            :ean="position?.EAN_Gebinde ?? undefined"
            title="Gebinde"
            :consistsOf="`${position?.Gebinhalt} ${position?.Einheit}`"
            label="Gebinde"
            :readonly="derivedReadonly"
            :allow-overshoot="picklistPicker.picklist?.allowOvershoot"
          />
          <qty-control
            v-if="qtyPaletten !== null"
            v-model="qtyPaletten"
            :spareQtys="spareQtysPaletten"
            :max="qtyPalettenMax ?? undefined"
            :qtyTotal="pickedPalettenCount ?? undefined"
            unit="Paletten"
            title="Paletten"
            :consistsOf="`${position?.Umrpalgebinde} ${position?.Gebindeinh}`"
            label="Paletten"
            :readonly="derivedReadonly"
            :allow-overshoot="picklistPicker.picklist?.allowOvershoot"
          />
        </v-card-text>
      </v-card>

      <v-card-text>
        <div class="mt-3">
          <div v-if="picklistPicker.picklist?.isUmbuchung">Abgehend</div>
          <v-select
            v-model="subsidiary"
            class="mb-3"
            :rules="[rules.ruleRequired]"
            :items="subsidiaryOptions"
            :hint="subsidiaryHintText"
            persistent-hint
            item-title="text"
            item-value="value"
            density="compact"
            required="true"
            :disabled="(derivedReadonly && !(picklistPicker.picklist?.isUmbuchung &&
             picklistPicker.selectedPosition === 0 && picklistPicker.picklist.allPositions.length === 1)) ||
             (picklistPicker.picklist?.isUmbuchung && picklistPicker.picklist.allPositions.length !== 1)"
            type="number"
            label="Niederlassung"
            :return-object="false"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props" :subtitle="item.raw.hint"></v-list-item>
            </template>
          </v-select>
          <v-select
            v-if="!warehouseLoadError"
            v-model="warehouse"
            class="mb-3"
            :rules="[rules.ruleRequired]"
            :items="warehouseOptions"
            item-title="text"
            item-value="value"
            :loading="warehousesLoading"
            density="compact"
            required="true"
            :disabled="derivedReadonly"
            type="number"
            label="Lagerhalle"
            :return-object="false"
          />
          <ErrorDisplay v-else :message="warehouseLoadError?.message" :loading="warehousesLoading"
                        @retry="loadWarehouses"/>
          <v-combobox
            v-if="!shelfLoadError"
            v-model="shelf"
            v-model:search="shelfSearch"
            class="mb-3"
            :items="shelfOptions"
            :loading="shelvesLoading"
            density="compact"
            :disabled="derivedReadonly"
            label="Lagerfach"
            placeholder="(keines)"
            :return-object="false"
            maxlength="10"
            :max="INT_MAX"
            item-title="value"
            item-value="value"
          >
            <template #append-inner>
              <div>
                {{ shelfName }}
              </div>
            </template>
          </v-combobox>
          <error-display v-else :message="shelfLoadError?.message" :loading="warehousesLoading" @retry="loadShelves"/>
        </div>
        <div class="mt-3" v-if="picklistPicker.picklist?.isUmbuchung">
          <div>Eingehend</div>
          <v-select
            v-model="destinationSubsidiary"
            class="mb-3"
            :rules="[rules.ruleRequired]"
            :items="subsidiaryOptions"
            :hint="destinationSubsidiaryHintText"
            persistent-hint
            item-title="text"
            item-value="value"
            density="compact"
            required="true"
            :disabled="(derivedReadonly && !(picklistPicker.picklist?.isUmbuchung &&
             picklistPicker.selectedPosition === 0 && picklistPicker.picklist.allPositions.length === 1)) ||
             (picklistPicker.picklist?.isUmbuchung && picklistPicker.picklist.allPositions.length !== 1)"
            type="number"
            label="Niederlassung"
            :return-object="false"
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props" :subtitle="item.raw.hint"></v-list-item>
            </template>
          </v-select>
          <v-select
            v-if="!warehouseLoadError"
            v-model="destinationWarehouse"
            class="mb-3"
            :rules="[rules.ruleRequired]"
            :items="destinationWarehouseOptions"
            item-title="text"
            item-value="value"
            :loading="warehousesLoading"
            density="compact"
            required="true"
            :disabled="derivedReadonly"
            type="number"
            label="Lagerhalle"
            :return-object="false"
          />
          <ErrorDisplay v-else :message="warehouseLoadError?.message" :loading="warehousesLoading"
                        @retry="loadDestinationWarehouses"/>
          <v-combobox
            v-if="!shelfLoadError"
            v-model="destinationShelf"
            v-model:search="destinationShelfSearch"
            class="mb-3"
            :items="destinationShelfOptions"
            :loading="shelvesLoading"
            density="compact"
            :disabled="derivedReadonly"
            label="Lagerfach"
            placeholder="(keines)"
            :return-object="false"
            maxlength="10"
            :max="INT_MAX"
            item-title="value"
            item-value="value"
          >
            <template #append-inner>
              <div>
                {{ destinationShelfName }}
              </div>
            </template>
          </v-combobox>
          <error-display v-else :message="shelfLoadError?.message" :loading="warehousesLoading"
                         @retry="loadDestinationShelves"/>
        </div>
        <component
          v-if="!picklist.isSuggestion"
          :is="picklist.isIncoming ? 'v-combobox' : 'v-autocomplete'"
          v-model="nve"
          v-model:search="nveSearch"
          label="NVE"
          required="true"
          :items="filteredNveOptionsLength <= 10 ? filteredNveOptions : (nve ? [nve] : [])"
          :no-data-text="filteredNveOptionsLength > 10 ? 'Bitte tippen Sie weiter' : undefined"
          filled
          type="number"
          :disabled="derivedReadonly"
          max="999999999999999999"
          :rules="[rules.ruleMaxLen18]"
          :return-object="false"
          item-title="text"
          item-value="value"
        />
        <v-combobox
          v-if="(!picklist.isIncoming && !picklist.isSuggestion) && position?.ChargenpflichtBool"
          :rules="[rules.ruleRequired, rules.ruleNumber, rules.ruleBatchNeedsToExist]"
          v-model="batchNoProxy"
          label="Chargennummer"
          required="true"
          :disabled="!picklist.isUmbuchung && !picklist.isCorrection && (derivedReadonly || (Boolean(nve) && !picklist.isIncoming))"
          :clearable="picklist.isUmbuchung || picklist.isCorrection"
          variant="filled"
          :bg-color="!batchNoInAvailable ? 'warning' : undefined"
          :prepend-inner-icon="!batchNoInAvailable ? 'mdi-alert-circle-outline' : undefined"
          :messages="
            !batchNoInAvailable
              ? 'Diese Chargennummer ist für dieses Lager nicht im System verzeichnet'
              : undefined
          "
          maxlength="9"
          :items="filteredBatchNoOptions"
          :return-object="false"
          @focusout="batchNo = batchNoProxyValue"
        >
        </v-combobox>
        <v-text-field
          v-if="!picklist.isSuggestion && position?.SeriennrpflichtBool"
          :rules="[rules.ruleRequired]"
          v-model="serialNo"
          label="Seriennummer"
          required="true"
          :disabled="derivedReadonly"
        />
        <v-switch v-if="picklist.isIncoming" label="NVE generieren" v-model="generateNve"/>
        <template v-if="picklist.isIncoming && position?.ChargenpflichtBool">
          <v-text-field
            clearable
            maxlength="50"
            label="Anerkennungsnummer"
            v-model="acknowledgementNumber"
            aria-autocomplete="none"
            autocomplete="off"
          />
          <v-text-field
            maxlength="30"
            label="Lieferanten-Chargen-Nr."
            v-model="dealerBatchNo"
            aria-autocomplete="none"
            autocomplete="off"
          />
          <v-text-field clearable :rules="[rules.ruleDate]" label="Haltbarkeitsdatum" v-model="bestBeforeDate"
                        type="date"/>
          <v-text-field clearable :rules="[rules.ruleDate]" label="Herstellungsdatum" v-model="manufacturingDate"
                        type="date"/>
        </template>
      </v-card-text>
    </v-form>
  </div>
</template>

<script lang="ts">
import QtyControl from './QtyControl.vue';
import ErrorDisplay from '@/components/ErrorDisplay.vue';
import {doAlert, doConfirm, makePercentage, parseGermanFloat, roundToOneOverX} from '@/util';
import {defineComponent, computed, ref, watch, nextTick, ComputedRef, VNodeRef} from 'vue';
import {PicklistPickerBooking, INT_MAX, usePicklistPickerStore} from '@/store/picklistPickerV2';
import {useSettings} from '@/store/settings';
import {VCombobox, VAutocomplete} from 'vuetify/components';
import Picklist, {ApiPicklistType} from '@/model/Picklist';
import Position from '@/model/Position';
import {getShelves, getWarehouses} from '@/api/module/warehouse';
import useSubsidiariesStore from '@/store/subsidiaries';

export default defineComponent({
  components: {
    QtyControl,
    VCombobox,
    VAutocomplete,
    ErrorDisplay,
  },
  props: {
    positionIndex: {
      type: Number,
      required: true,
    },
    picklist: {
      type: Picklist,
      required: true,
    },
    bookingIndex: {
      type: Number,
      required: true,
    },
    readonly: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    deletable: {
      type: Boolean,
      default: false,
    },
  },
  emits: {
    resetPosition() {
      return true;
    },
    deletePosition() {
      return true;
    },
  },
  setup(props, ctx) {
    const settingsStore = useSettings();
    const subsidiariesStore = useSubsidiariesStore();
    const position: ComputedRef<Position | undefined> = computed(() => props.picklist.allPositions[props.positionIndex]);
    const picklistPicker = usePicklistPickerStore();
    const pickData = computed(() => position.value ? picklistPicker.getPickData(position.value) : null);
    const bookingData = computed(() => {
      if (!position.value) return null;
      try {
        return picklistPicker.getBooking(position.value, props.bookingIndex);
      } catch {
        return null;
      }
    });

    // This part is real hacky shit, the combox changes it's v-model after pressing a number not 0. this validation executes checks,
    // which will close the booking item if the qty is max. with this hacky stuff, the value of the combox will be saved
    // in this variable and after unfocus the combobox control, we will put it to the real variable
    const batchNoProxyValue = ref<number | null | undefined>(null);
    const batchNoProxy = computed({
      get(): number | null | undefined {
        if (batchNo.value) {
          return batchNo.value;
        }
        return batchNoProxyValue.value;
      },
      set(val: number | undefined | null) {
        if (!val) {
          batchNoProxyValue.value = null;
          return;
        }

        batchNoProxyValue.value = Number(val);
      },
    });

    function computedBookingProperty<K extends keyof PicklistPickerBooking>(field: K) {
      return computed({
        get(): PicklistPickerBooking[K] | undefined {
          return bookingData.value?.[field];
        },
        set(val?: PicklistPickerBooking[K]) {
          if (!position.value) return;
          picklistPicker.modify({
            position: position.value,
            bookingIndex: props.bookingIndex,
            [field]: val,
          });
        },
      });
    }

    const qty = computed({
      get() {
        return bookingData.value?.qty;
      },
      set(val: number | undefined) {
        if (!position.value) return;
        picklistPicker.modify({
          position: position.value,
          bookingIndex: props.bookingIndex,
          qty: roundToOneOverX(val!, 1000),
        });
      },
    });

    const nve = computedBookingProperty('nve');
    const batchNo = computedBookingProperty('batchNo');
    const serialNo = computedBookingProperty('serialNo');
    const warehouse = computedBookingProperty('warehouse');
    const subsidiary = computedBookingProperty('subsidiary');
    const subsidiaryHintText = computed(()=> {
      if (!subsidiariesStore.data || subsidiariesStore.data.length === 0) return '';
      const selected = subsidiariesStore.data.find((sub) => sub.NiederlassungNr === subsidiary.value);
      if (!selected?.Bezeichnung || selected?.Bezeichnung === '') return '';
      return `${selected.Bezeichnung}`;
    });
    const shelfRaw = computedBookingProperty('shelf');
    const shelf = computed({
      get: () => shelfRaw.value,
      set: (newVal: string | number | null | undefined) => {
        if (newVal === null || newVal === undefined || newVal === '') return shelfRaw.value = null;

        const numberVal = Number(newVal);
        if (Number.isNaN(numberVal)) {
          shelfRaw.value = null;
          return;
        }

        shelfRaw.value = numberVal;
      },
    });
    const shelfName = computed(() => shelfOptions.value.find(({value}) => String(value) === String(shelf.value))?.text);
    const shelfSearchRaw = ref<string | null>(null);
    const shelfSearch = computed({
      get: () => shelfSearchRaw.value,
      set: async (newVal: string | number | null | undefined) => {
        if (newVal === null || newVal === undefined) return shelfSearchRaw.value = null;

        let calculatedValue = newVal.toString().replaceAll(/\D/g, '');
        let numberVal: number;
        do {
          numberVal = Number(calculatedValue);
          if (Number.isNaN(numberVal) || numberVal > INT_MAX) {
            calculatedValue = calculatedValue.substring(0, calculatedValue.length - 1);
            continue;
          }

          break;
        } while (calculatedValue.length > 0);

        if (shelfSearchRaw.value === calculatedValue) {
          shelfSearchRaw.value = null;
          await nextTick();
        }
        shelfSearchRaw.value = calculatedValue;
      },
    });

    const destinationWarehouse = computedBookingProperty('destinationWarehouse');
    const destinationSubsidiary = computedBookingProperty('destinationSubsidiary');
    const destinationSubsidiaryHintText = computed(()=> {
      if (!subsidiariesStore.data || subsidiariesStore.data.length === 0) return '';
      const selected = subsidiariesStore.data.find((sub) => sub.NiederlassungNr === destinationSubsidiary.value);
      if (!selected?.Bezeichnung || selected?.Bezeichnung === '') return '';
      return `${selected.Bezeichnung}`;
    });

    if (props.picklist.isUmbuchung) {
      watch(subsidiary, () => {
        if (subsidiary.value != null) {
          props.picklist.NiederlassungAbgehend!.Niederlassung = subsidiary.value;
          props.picklist.NiederlassungAbgehend!.Name1 =subsidiariesStore.data.find((sub) => sub.NiederlassungNr === subsidiary.value)?.Name1 ?? '';
        }
      });

      watch(destinationSubsidiary, () => {
        if (destinationSubsidiary.value != null) {
          props.picklist.NiederlassungEingehend!.Niederlassung = destinationSubsidiary.value;
          props.picklist.NiederlassungEingehend!.Name1 = subsidiariesStore.data.find((sub) => sub.NiederlassungNr === destinationSubsidiary.value)?.Name1 ?? '';
        }
      });
    }

    console.log('dest: ', destinationSubsidiary.value, 'pick: ', picklistPicker.picklist);
    const destinationShelfRaw = computedBookingProperty('destinationShelf');
    const destinationShelf = computed({
      get: () => destinationShelfRaw.value,
      set: (newVal: string | number | null | undefined) => {
        if (newVal === null || newVal === undefined || newVal === '') return destinationShelfRaw.value = null;

        const numberVal = Number(newVal);
        if (Number.isNaN(numberVal)) {
          destinationShelfRaw.value = null;
          return;
        }

        destinationShelfRaw.value = numberVal;
      },
    });
    const destinationShelfName = computed(() => destinationShelfOptions.value.find(({value}) => {
      return String(value) === String(destinationShelf.value);
    })?.text);
    const destinationShelfSearchRaw = ref<string | null>(null);
    const destinationShelfSearch = computed({
      get: () => destinationShelfSearchRaw.value,
      set: async (newVal: string | number | null | undefined) => {
        if (newVal === null || newVal === undefined) return destinationShelfSearchRaw.value = null;

        let calculatedValue = newVal.toString().replaceAll(/\D/g, '');
        let numberVal: number;
        do {
          numberVal = Number(calculatedValue);
          if (Number.isNaN(numberVal) || numberVal > INT_MAX) {
            calculatedValue = calculatedValue.substring(0, calculatedValue.length - 1);
            continue;
          }

          break;
        } while (calculatedValue.length > 0);

        if (destinationShelfSearchRaw.value === calculatedValue) {
          destinationShelfSearchRaw.value = null;
          await nextTick();
        }
        destinationShelfSearchRaw.value = calculatedValue;
      },
    });


    const generateNve = computedBookingProperty('generateNve');
    const acknowledgementNumber = computedBookingProperty('acknowledgementNumber');
    const dealerBatchNo = computedBookingProperty('dealerBatchNo');
    const bestBeforeDate = computedBookingProperty('bestBeforeDate');
    const manufacturingDate = computedBookingProperty('manufacturingDate');

    const qtyGebinde = computed({
      get: () => bookingData.value?.qtyGebinde ? roundToOneOverX(bookingData.value.qtyGebinde, 1000) : bookingData.value?.qtyGebinde,
      set: (data: number | null | undefined) => (typeof data === 'number' && typeof position.value?.Gebinhalt === 'number' ? qty.value = Math.round(data * position.value.Gebinhalt) : null),
    });
    const qtyPaletten = computed({
      get: () => bookingData.value?.qtyPaletten ? roundToOneOverX(bookingData.value.qtyPaletten, 1000) : bookingData.value?.qtyPaletten,
      set: (data: number | null | undefined) => (typeof data === 'number' && typeof position.value?.mengeProPalette === 'number' ? qty.value = Math.round(data * position.value.mengeProPalette) : null),
    });

    const spare = computed(() => position.value?.Gebinhalt ? roundToOneOverX(qty.value ?? 0 % position.value.Gebinhalt, 1000) : null);
    const spareGebinde = computed(
        () => position.value?.mengeProPalette && position.value.Gebinhalt ?
        Math.floor((qty.value ?? 0 % position.value.mengeProPalette) / position.value.Gebinhalt) :
        null,
    );

    const subsidiaryOptions = computed(() => subsidiariesStore.data.map((s) => ({
      text: `${s.Name1}${s.NiederlassungNr === settingsStore.selectedSubsidiary ? ' (Standard)' : ''}`,
      value: s.NiederlassungNr,
      hint: s.Bezeichnung,
    })));

    const warehouseOptions = ref<Array<{ value: number; text: string }>>([]);
    const warehouseLoadError = ref<Error | null>(null);
    const warehousesLoading = ref(false);

    const loadWarehouses = async () => {
      warehousesLoading.value = true;
      warehouseOptions.value = [];
      try {
        const [warehouses] = await getWarehouses({Niederlassung: subsidiary.value as number | undefined}, []);

        warehouseOptions.value = warehouses.map((w) => ({
          text: `${w.Bezeichnung}${w.Lagerhalle === settingsStore.selectedWarehouse?.Lagerhalle ? ' (Standard)' : ''}`,
          value: w.Lagerhalle,
        }));
        warehouseLoadError.value = null;
      } catch (err) {
        warehouseLoadError.value = err as Error;
      } finally {
        warehousesLoading.value = false;
      }
    };

    const destinationWarehouseOptions = ref<Array<{ value: number; text: string }>>([]);

    const loadDestinationWarehouses = async () => {
      warehousesLoading.value = true;
      destinationWarehouseOptions.value = [];
      try {
        const [warehouses] = await getWarehouses({Niederlassung: destinationSubsidiary.value as number | undefined}, []);
        destinationWarehouseOptions.value = warehouses.map((w) => ({
          text: `${w.Bezeichnung}${w.Lagerhalle === settingsStore.selectedWarehouse?.Lagerhalle ? ' (Standard)' : ''}`,
          value: w.Lagerhalle,
        }));
        warehouseLoadError.value = null;
      } catch (err) {
        warehouseLoadError.value = err as Error;
      } finally {
        warehousesLoading.value = false;
      }
    };

    watch(subsidiary, loadWarehouses);
    watch(destinationSubsidiary, loadDestinationWarehouses);
    loadWarehouses();
    loadDestinationWarehouses();

    const shelfOptions = ref<Array<{ text: string; value: number }>>([]);
    const destinationShelfOptions = ref<Array<{ text: string; value: number }>>([]);
    const shelfLoadError = ref<Error | null>(null);
    const shelvesLoading = ref(false);

    const loadShelves = async () => {
      shelvesLoading.value = true;
      shelfOptions.value = [];
      try {
        const [shelves] = await getShelves({Niederlassung: subsidiary.value as number | undefined, Lagerhalle: warehouse.value!}, []);

        shelfOptions.value = shelves.map((s) => ({text: s.Bezeichnung, value: s.Lagerfach}));
        shelfLoadError.value = null;
      } catch (err) {
        shelfLoadError.value = err as Error;
      } finally {
        shelvesLoading.value = false;
      }
    };

    const loadDestinationShelves = async () => {
      shelvesLoading.value = true;
      destinationShelfOptions.value = [];
      try {
        const [shelves] = await getShelves({
          Niederlassung: destinationSubsidiary.value as number | undefined,
          Lagerhalle: destinationWarehouse.value,
        }, []);

        destinationShelfOptions.value = shelves.map((s) => ({text: s.Bezeichnung, value: s.Lagerfach}));
        shelfLoadError.value = null;
      } catch (err) {
        shelfLoadError.value = err as Error;
      } finally {
        shelvesLoading.value = false;
      }
    };

    watch(computed(() => [subsidiaryOptions.value, warehouseOptions.value]), loadShelves, {immediate: true});
    watch(computed(() => [subsidiaryOptions.value, destinationWarehouseOptions.value]), loadDestinationShelves, {immediate: true});

    const nveSearch = ref<string | null | number>('');

    watch(nveSearch, (newSearch) => {
      if (!newSearch) return;
      if (newSearch.toString().length > 18) nextTick().then(() => nveSearch.value = newSearch.toString().substring(0, 18));
    });
    const nveOptions = computed(
        () => {
          const available = new Set(
              position.value?.VerfuegbareNVEs
                  .map((w) => w.NVE)
                  .filter((w) => !Object.values(picklistPicker.picked).some((d) => d?.bookings.some((b) => b.nve === w))),
          );

          return Array.from(available.values()).map((w) => ({
            text: w,
            value: w,
          }));
        },
    );
    const batchNoSearchValue = ref<number | null>(null);
    const batchNoSearch = computed({
      set: (v: string | null) => batchNoSearchValue.value = Number(v),
      get: () => batchNoSearchValue.value ? String(batchNoSearchValue.value) : null,
    });
    const batchNoOptions = computed<number[]>(() => position.value?.Chargen.map((w) => (w.Chargennr)) ?? []);

    const percentage = computed(() => makePercentage(qty.value ?? 0, position.value?.Menge ?? 0));

    const rules = {
      ruleQty(val: string): string | boolean {
        const parsedVal = parseFloat(val);

        if (parsedVal > (position.value?.Menge ?? 0)) return 'zu groß';
        if (parsedVal < 0) return 'zu klein';

        return true;
      },
      ruleGebindeQty(val: string): string | boolean {
        const parsedVal = parseFloat(val);

        if (!position.value?.Gebindemenge) return 'das sollte nicht passieren';

        if (parsedVal > position.value.Gebindemenge) return 'zu groß';
        if (parsedVal < 0) return 'zu klein';

        return true;
      },
      rulePalettenQty(val: string): string | boolean {
        const parsedVal = parseFloat(val);

        if (!position.value?.Palettenmenge) return 'das sollte nicht passieren';

        if (parsedVal > position.value.Palettenmenge) return 'zu groß';
        if (parsedVal < 0) return 'zu klein';

        return true;
      },
      ruleRequired(val: any): string | boolean {
        if (!val && val !== 0) return 'erforderlich';

        return true;
      },
      ruleMoreThanZero(val: any): string | boolean {
        if (!val) return 'erforderlich';

        return true;
      },
      ruleNumber(val: any): string | boolean {
        val = parseGermanFloat(val);
        if (typeof val !== 'number' || Number.isNaN(val)) return 'bitte eine Zahl eingeben';

        return true;
      },
      ruleBatchNeedsToExist(val: any): string | boolean {
        if ((props.picklist.isUmbuchung || props.picklist.isCorrection) && val) {
          if (!batchNoOptions.value.find((b) => b === batchNo.value)) return 'Die Chargen-Nr. konnte nicht gefunden werden.';
        }
        return true;
      },
      ruleInt(val: string | number | null | undefined): string | boolean {
        if (!val) return true;
        if (parseInt(val as string).toString() !== val) return 'bitte eine ganze Zahl eingeben';

        return true;
      },
      ruleDate(val: string | null | undefined): string | true {
        if (!val) return true;

        if (!/[12]\d{3}-((0[1-9])|(1[0-2]))-(([0-2]\d)|(3[01]))/.exec(val)) return 'bitte ein gültiges Datum eingeben';

        return true;
      },
      ruleMaxLen18(val: string | null | undefined): string | true {
        const strVal = val?.toString();
        if ((strVal?.length ?? 0) > 18) return 'maximal 18 Zeichen';
        return true;
      },
    };

    const resetPosition = async () => {
      if (!await doConfirm('Wollen Sie diese Position wirklich zurücksetzen?', 'Wirklich zurücksetzen?')) return;

      ctx.emit('resetPosition');
    };
    const addAll = () => {
      // if (!confirm('Wollen Sie die Menge dieser Position wirklich vervollständigen?')) return;

      if (qty.value === undefined) return;

      qty.value += pickData.value?.qtyLeft ?? 0;
    };
    const pickManually = async () => {
      try {
        const ean = position.value?.EAN_Gebinde ?? position.value?.Eancode;
        if (!ean) return;
        await picklistPicker.modifyByScan({
          ean,
          bookingIndex: props.bookingIndex,
        });
      } catch (err: any) {
        await doAlert(err.message, 'Fehler');
      }
    };

    const form = ref<HTMLFormElement & VNodeRef | undefined>();
    watch(form, (f) => {
      f?.validate();
    });


    watch(bookingData, async () => {
      await nextTick();
      form.value?.validate();
    });

    const derivedReadonly = computed(() => props.readonly ?? picklistPicker.readonly);
    const spareQtysGebinde = computed(() => spare.value ? [`${spare.value} ${position.value?.Einheit}`] : []);
    const spareQtysPaletten = computed(() => spareGebinde.value ? [...spareQtysGebinde.value, `${spareGebinde.value} ${position.value?.Gebindeinh}`] : spareQtysGebinde.value);

    const qtyMax = computed(() => {
      if (pickData.value?.position.Menge === 0) return 0;
      if (qty.value === undefined || pickData.value === null) return 0;
      return qty.value + pickData.value.qtyLeft;
    });
    const qtyGebindeMax = computed(() => {
      if ((pickData.value?.position.Gebindemenge ?? null) === null) return null;
      if (pickData.value?.position.Menge === 0) return 0;
      return typeof qtyGebinde.value === 'number' && typeof pickData.value?.qtyGebindeLeft === 'number' ? roundToOneOverX(qtyGebinde.value + (pickData.value.qtyGebindeLeft ?? 0), 1000) : null;
    });
    const qtyPalettenMax = computed(() => {
      if ((pickData.value?.position.Palettenmenge ?? null) === null) return null;
      if (pickData.value?.position.Menge === 0) return 0;
      return typeof qtyPaletten.value === 'number' && typeof pickData.value?.qtyPalettenLeft === 'number' ? roundToOneOverX(qtyPaletten.value + (pickData.value.qtyPalettenLeft ?? 0), 1000) : null;
    });

    const deletePosition = async () => {
      if (!await doConfirm('Soll diese Position wirklich gelöscht werden?', 'Wirklich löschen?')) return;
      ctx.emit('deletePosition');
    };

    const pickedCount = computed(() => {
      return pickData.value?.bookings?.reduce((cum, b) => cum + b.qty, 0) ?? 0;
    });
    const pickedGebindeCount = computed(() => {
      return position?.value?.Gebindemenge;
    });
    const pickedPalettenCount = computed(() => {
      return position?.value?.Palettenmenge;
    });


    const hasSubbookings = computed(() => {
      return pickData.value && pickData.value?.bookings?.length > 1;
    });

    const filteredNveOptions = computed(
        () => [
          ...nveOptions.value.filter((v) => !nveSearch.value || v.text.indexOf(String(nveSearch.value)) !== -1),
          ...(nve.value ? [nve.value] : []),
        ],
    );

    const filteredNveOptionsLength = computed(() => filteredNveOptions.value.length);

    const filteredBatchNoOptions = computed(
        () => batchNoProxy.value ? String(batchNoProxy.value).length > 3 ? batchNoOptions.value : undefined : undefined);

    return {
      position,
      pickData,
      pickedCount,
      pickedGebindeCount,
      pickedPalettenCount,
      bookingData,
      hasSubbookings,
      qty,
      nve,
      batchNo,
      serialNo,
      warehouse,
      subsidiary,
      subsidiaryOptions,
      shelf,
      shelfSearch,
      qtyGebinde,
      qtyPaletten,
      spare,
      spareGebinde,
      warehouseOptions,
      warehousesLoading,
      warehouseLoadError,
      loadWarehouses,
      shelfOptions,
      shelvesLoading,
      shelfName,
      shelfLoadError,
      loadShelves,
      nveOptions,
      INT_MAX,
      filteredNveOptions,
      filteredNveOptionsLength,
      batchNoOptions,
      percentage,
      rules,
      derivedReadonly,
      batchNoInAvailable: computed(() => !!batchNoOptions.value?.find((b: number) => b === batchNo.value)),
      resetPosition,
      deletePosition,
      addAll,
      pickManually,
      subsidiaryHintText,
      destinationSubsidiaryHintText,
      form,
      spareQtysGebinde,
      spareQtysPaletten,
      qtyMax,
      qtyGebindeMax,
      qtyPalettenMax,
      ApiPicklistType,
      generateNve,
      acknowledgementNumber,
      filteredBatchNoOptions,
      dealerBatchNo,
      bestBeforeDate,
      manufacturingDate,
      nveSearch,
      batchNoSearch,
      picklistPicker,
      batchNoProxy,
      batchNoProxyValue,
      destinationSubsidiary,
      destinationShelf,
      destinationShelfSearch,
      destinationWarehouse,
      destinationShelfName,
      destinationWarehouseOptions,
      destinationShelfOptions,
      loadDestinationWarehouses,
      loadDestinationShelves,
    };
  },
});
</script>

<style lang="scss">
.v-application .booking-item .error--text {
  color: #415564 !important;
}
</style>
<style lang="scss" scoped>
.qty-header {
  margin-right: 120px;
}
</style>
